import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import { isTheDropSelected } from '@exp/components/products/theDropUtils';
import isGhost from '@exp/utilities/isGhost';

import {
  extendIssuedCoupon,
  fetchAttachments,
  fetchCheckoutData,
  placeOrder,
  removeAddress,
  removeAttachment,
  removeSavedPayment,
  saveAddress,
  selectSavedPayment,
  setActiveCoupon,
  setBraintreeToken,
  setCartSetKey,
  setCouponFromCart,
  setCouponId,
  setCreditCardDetails,
  setFFLUploaded,
  setPaymentAsDefault,
  setPaymentManagerEditing,
  setPrizeId,
  setShippingAddress,
  setShippingMethod,
  setShippingReady,
  uploadAttachment,
} from './singleCheckoutActions';

import { calculateCompatibilityWarning, deconstructName, isAddress, isAddressEquals, isSavedAddress } from './utils/addressUtils';
import { ERROR_CODE, type ErrorCode, ROOT_PAYMENT_TYPES } from '../js/common/constants';
import { listenerMiddleware } from '../redux/middleware';
import { type RootState } from '../redux/store';
import BraintreeConfigurator from '../js/common/utils/braintreeConfigurator';
import deriveRestrictions from './utils/deriveRestrictions';
import deriveShipments from './utils/deriveShipments';
import type SingleCheckoutTypes from './singleCheckoutTypes';

export type SingleCheckoutState = {
  addresses?: SingleCheckoutTypes.ShippingAddress[] | SingleCheckoutTypes.SavedShippingAddress[];
  attachedFiles: SingleCheckoutTypes.AttachedFile[];
  /** a checkout-blocking error that requires a full-stop notice, like fraud, failed payments, etc */
  blockingError?: SingleCheckoutTypes.ReviewApiResponseError;
  cartSetKey?: string;
  checkoutData?: SingleCheckoutTypes.ReviewApiResponse;
  /** was the current couponId set from the cart */
  couponFromCart?: boolean;
  couponId?: number;
  prizeId?: number;
  /** user confirmation that required files have been uploaded */
  fflConfirmed?: boolean;
  fflRequired?: boolean;
  hadSavedAddresses: boolean;
  isGhosting?: boolean;
  isTheDropSelected?: boolean;
  pendingFetchCount: number;
  /** the order details after a successful submitOrder call */
  placedOrder?: SingleCheckoutTypes.PlaceOrderApiResponse;
  restrictions?: SingleCheckoutTypes.ShippingRestrictions;
  selections: {
    payment: {
      /** held onto so the user can edit their card details without re-typing everything */
      ccInfo?: {
        billingAddress?: SingleCheckoutTypes.UserEnteredAddress;
        id?: SingleCheckoutTypes.SavedPaymentId;
        rawCardDetails?: SingleCheckoutTypes.RawCreditCardDetails;
        useShippingAddress?: boolean;
      };
      /** the actual payment data sent to place order */
      payment?: SingleCheckoutTypes.ReviewApiRequestPayment;
      paymentDescriptor?: SingleCheckoutTypes.SelectedPaymentDescriptor;
    };
    paymentManagerEditing?: boolean;
    shippingAddress?: SingleCheckoutTypes.ShippingAddress | SingleCheckoutTypes.SavedShippingAddress;
    shippingMethods?: {
      cartIds: SingleCheckoutTypes.CartId[];
      methodId: SingleCheckoutTypes.ShippingMethodId;
    }[];
    shippingReady?: boolean;
  };
  shipments?: {
    cartIds: SingleCheckoutTypes.CartId[];
    compatibleMethods: SingleCheckoutTypes.ShippingMethod[];
    cost: number;
    includesSurcharges: boolean;
    items: SingleCheckoutTypes.CartItem[];
    key: string;
    messages?: string[];
    /** if there are multiple shipments from the same org */
    multi?: boolean;
    /** if there are multiple shipments from the same org, this is the 1-based index */
    multiIndex?: number;
    orgImageKey: string;
    orgImageType: string;
    orgName: string;
    selectedMethod: SingleCheckoutTypes.ShippingMethod;
    shipMethods: SingleCheckoutTypes.ShippingMethod[];
  }[];
  /** true if any shipment needs an address before costs can be calculated */
  shipmentsNeedsAddress: boolean;
  totalItemQuantity?: number;
};

const initialState: SingleCheckoutState = {
  attachedFiles: [],
  pendingFetchCount: 0,
  hadSavedAddresses: false,
  isGhosting: isGhost(),
  selections: {
    payment: {},
  },
  shipmentsNeedsAddress: true,
};

const singleCheckoutReducer = createSlice({
  name: 'singleCheckout',
  initialState,
  reducers: {
    decrementPendingFetchCount(state) {
      state.pendingFetchCount -= 1;
    },
    incrementPendingFetchCount(state) {
      state.pendingFetchCount += 1;
    },
    setBlockingError(state, action: PayloadAction<SingleCheckoutTypes.ReviewApiResponseError>) {
      state.blockingError = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchAttachments.fulfilled, (state, action) => {
      state.attachedFiles = action.payload?.data || [];
    });

    builder.addCase(fetchCheckoutData.fulfilled, (state, action) => {
      state.checkoutData = action.payload;

      state.restrictions = deriveRestrictions(action.payload);

      state.fflRequired = state.restrictions?.fflRequired;

      deriveShipments(state);

      if (!state.selections.shippingAddress && action.payload?.shipping) {
        const compatibilityWarning = calculateCompatibilityWarning(action.payload.shipping, state.restrictions);

        // Default the selected shipping address
        state.selections.shippingAddress = {
          ...deconstructName(action.payload.shipping.name),
          ...action.payload.shipping,
          compatibilityWarning,
          compatible:
            (action.payload.shipping as SingleCheckoutTypes.SavedShippingAddress)?.compatible &&
            !compatibilityWarning,
        };
        state.selections.shippingReady = !!(action.payload.shipping as SingleCheckoutTypes.SavedShippingAddress)?.id;
      }

      if (!state.addresses) {
        // Initialize the saved addresses the first load
        state.addresses = (action.payload?.shippingRestrictions?.addresses || [])
          .map((address) => {
            const compatibilityWarning = calculateCompatibilityWarning(address, state.restrictions);

            return {
              ...deconstructName(address.name),
              ...address,
              compatibilityWarning,
              compatible: address.compatible && !compatibilityWarning,
            };
          });
        state.hadSavedAddresses = !!state.addresses?.length;
      }

      state.selections.payment.paymentDescriptor = action.payload.payment;

      if (!state.selections.payment.payment && action.payload?.payment?.id) {
        // default the selected saved payment
        state.selections.payment.payment = {
          id: action.payload.payment.id,
          type: ROOT_PAYMENT_TYPES.CREDIT_CARD,
        };
      }

      // clear out saved payments if configuration indicates not saveable (for ffl)
      if (!action.payload?.paymentOptions?.CREDIT_CARD?.saveable) {
        state.checkoutData.savedPayments = [];
      }

      // @ts-expect-error proptypes / ts mismatch
      state.isTheDropSelected = isTheDropSelected(state.checkoutData.coupons, state.couponId);
    });

    builder.addCase(fetchCheckoutData.rejected, (state, action) => {
      const errorCodes = action.payload?.errorCodes;
      if (errorCodes?.includes(ERROR_CODE.COUPON_INVALID)) {
        state.couponFromCart = false;
        state.couponId = undefined;
        state.isTheDropSelected = false;
      }

      if (errorCodes?.includes(ERROR_CODE.PRIZE_INVALID)) {
        state.prizeId = undefined;
      }
    });

    builder.addCase(placeOrder.fulfilled, (state, action) => {
      state.placedOrder = action.payload;
    });

    builder.addCase(removeAddress.fulfilled, (state, action) => {
      const { arg: address } = action.meta;
      state.addresses = state.addresses.filter((a) => !isAddress(a, address));
      if (!state.addresses.length) {
        state.hadSavedAddresses = false;
      }

      if (isAddress(address, state.selections.shippingAddress)) {
        state.selections.shippingAddress = undefined;
        state.selections.shippingReady = false;
      }
    });

    builder.addCase(removeAttachment.fulfilled, (state, action) => {
      state.attachedFiles = state.attachedFiles.filter((file) => file.id !== action.meta.arg);

      if (!state.attachedFiles.length) {
        state.fflConfirmed = false;
      }
    });

    builder.addCase(removeSavedPayment.fulfilled, (state, action) => {
      const savedPaymentId = action.meta.arg;

      if (state.selections.payment.payment?.id === savedPaymentId) {
        state.selections.payment = {};
      }
    });

    builder.addCase(saveAddress.fulfilled, (state, action) => {
      if (action.payload.primary) {
        // Clear the primary flag on any existing addresses
        state.addresses = state.addresses.map((a) => (a.primary ? ({
          ...a,
          primary: false,
        }) : a));
      }

      if (action.meta.arg.id) {
        state.addresses = state.addresses.map((a) => (a.id === action.meta.arg.id ? action.payload : a));
      } else {
        // Insert the new address after the primary address
        const primaryIndex = state.addresses.findIndex((a) => a.primary);
        state.addresses.splice(primaryIndex + 1, 0, action.payload);
      }
    });

    builder.addCase(selectSavedPayment, (state, action) => {
      const selectedPaymentId = action.payload;

      // drop the ccInfo if switching to a different saved payment. the token gets lost and we'd need to either
      // keep it around or try to finagle a reauth if they re-select the same card. the card wouldnt actually be saved
      // if they edited it but didnt checkout with it anyway.
      if (selectedPaymentId !== state.selections.payment.payment?.id) {
        state.selections.payment.ccInfo = undefined;
      }

      state.selections.payment.payment = {
        id: selectedPaymentId,
        type: ROOT_PAYMENT_TYPES.CREDIT_CARD,
      };
    });

    builder.addCase(setBraintreeToken, (state, action) => {
      const payment = BraintreeConfigurator.tokenToOrderPayment(action.payload);

      state.blockingError = undefined;

      state.selections.payment = {
        payment,
      };
    });

    builder.addCase(setCartSetKey, (state, action) => {
      state.cartSetKey = action.payload;
    });

    builder.addCase(setCouponFromCart, (state, action) => {
      state.couponFromCart = action.payload;
    });

    builder.addCase(setActiveCoupon, (state, action: PayloadAction<{ couponId?: number; prizeId?: number }>) => {
      state.couponId = action.payload.couponId;
      state.prizeId = action.payload.prizeId;
    });

    builder.addCase(setCouponId, (state, action) => {
      state.couponId = action.payload;
    });

    builder.addCase(setPrizeId, (state, action) => {
      state.prizeId = action.payload;
    });

    builder.addCase(setCreditCardDetails.fulfilled, (state, action) => {
      const {
        selections: {
          payment,
        },
      } = state;

      state.blockingError = undefined;

      state.selections.payment = {
        ...payment,
        ccInfo: action.meta.arg,
        payment: {
          id: action.meta.arg.id,
          key: action.payload,
          primary: action.meta.arg.primary,
          save: action.meta.arg.save,
          type: ROOT_PAYMENT_TYPES.CREDIT_CARD,
        },
      };
    });

    builder.addCase(setFFLUploaded, (state, action) => {
      state.fflConfirmed = action.payload;
    });

    builder.addCase(setPaymentAsDefault, (state, action) => {
      if (state.selections.payment.payment.type === ROOT_PAYMENT_TYPES.CREDIT_CARD) {
        state.selections.payment.payment.primary = action.payload;
      }
    });

    builder.addCase(setPaymentManagerEditing, (state, action) => {
      state.selections.paymentManagerEditing = action.payload;
    });

    builder.addCase(setShippingAddress, (state, action) => {
      const {
        selections: {
          payment: {
            paymentDescriptor,
          },
        },
      } = state;

      if (!isAddressEquals(action.payload, state.selections.shippingAddress)) {
        state.selections.shippingMethods = undefined;
      }

      state.selections.shippingAddress = action.payload;

      deriveShipments(state);

      if (isSavedAddress(action.payload) && !action.payload.paymentVerified && paymentDescriptor?.id) {
        state.selections.payment = {};
      }
    });

    builder.addCase(setShippingMethod, (state, action) => {
      deriveShipments(state, action.payload);
    });

    builder.addCase(setShippingReady, (state, action) => {
      state.selections.shippingReady = action.payload;
    });

    builder.addCase(uploadAttachment.fulfilled, (state, action) => {
      if (action.payload?.data) {
        state.attachedFiles.push(action.payload.data);
      }
    });
  },
});

listenerMiddleware.startListening({
  actionCreator: fetchCheckoutData.rejected,
  effect: async (action, listenerApi) => {
    const errorCodes = action.payload?.errorCodes;

    // retry without the coupon/prize if it's invalid
    if (
      errorCodes?.includes(ERROR_CODE.COUPON_INVALID) ||
      errorCodes?.includes(ERROR_CODE.PRIZE_INVALID)
    ) {
      listenerApi.dispatch(fetchCheckoutData());
    }
  },
});

// refresh checkout data after a few actions
listenerMiddleware.startListening({
  predicate: (action) => [
    extendIssuedCoupon.fulfilled,
    removeSavedPayment.fulfilled,
    selectSavedPayment,
    setActiveCoupon,
    setBraintreeToken,
    setCreditCardDetails.fulfilled,
    setShippingMethod,
  ].some(({ match }) => match(action)),
  effect: async (action, listenerApi) => {
    const { singleCheckout: { cartSetKey, checkoutData } } = listenerApi.getState() as RootState;

    const shouldRefresh = cartSetKey && checkoutData;

    if (!shouldRefresh) {
      return;
    }

    listenerApi.dispatch(fetchCheckoutData());
  },
});

listenerMiddleware.startListening({
  predicate: (action) => action.type.startsWith('singleCheckout/') && action.type.endsWith('/pending'),
  effect: async (action, listenerApi) => {
    listenerApi.dispatch(singleCheckoutReducer.actions.incrementPendingFetchCount());
  },
});

listenerMiddleware.startListening({
  predicate: (action) => action.type.startsWith('singleCheckout/') && (action.type.endsWith('/rejected') || action.type.endsWith('/fulfilled')),
  effect: async (action, listenerApi) => {
    listenerApi.dispatch(singleCheckoutReducer.actions.decrementPendingFetchCount());
  },
});

const bailCodes = new Set<ErrorCode>([
  ERROR_CODE.INVALID_SET,
  ERROR_CODE.KEY_NOT_FOUND,
  ERROR_CODE.ORDER_RULE_VIOLATION,
]);

listenerMiddleware.startListening({
  predicate: (action) => [
    fetchCheckoutData.rejected.type,
    placeOrder.rejected.type,
  ].includes(action.type),
  effect: async (action, listenerApi) => {
    const payload = action.payload as { errorCodes?: string[] } | undefined;
    if (payload?.errorCodes?.some((code: ErrorCode) => bailCodes.has(code))) {
      window.location.href = '/cart';
    } else {
      listenerApi.dispatch(singleCheckoutReducer.actions.setBlockingError(
        action.payload as SingleCheckoutTypes.ReviewApiResponseError,
      ));
    }
  },
});

export default singleCheckoutReducer.reducer;
